import api from './api';

/**
 * Simple Verification Service
 * API calls for General Verifier workflow
 */
class SimpleVerificationService {
  /**
   * Generate checklist from AI conversation
   */
  async generateChecklist(conversationHistory, projectType) {
    const response = await api.post(
      '/simple-verifications/ai/generate-checklist',
      { conversationHistory, projectType }
    );
    return response.data;
  }

  /**
   * Create simple verification request
   */
  async createVerification(data) {
    const response = await api.post(
      '/simple-verifications/create',
      data
    );
    return response.data;
  }

  /**
   * Whether the verifier can accept work (has an approved government ID).
   * Drives the identity-document upload banner.
   */
  async getEligibility() {
    const response = await api.get('/simple-verifications/eligibility');
    return response.data;
  }

  /**
   * Get available verifications for verifier
   */
  async getAvailableVerifications(lat, lng, radius = 50, city = null) {
    const params = { radius };
    if (lat && lng) {
      params.lat = lat;
      params.lng = lng;
    }
    if (city) {
      params.city = city;
    }
    const response = await api.get(
      '/simple-verifications/available',
      { params }
    );
    return response.data;
  }

  /**
   * Accept verification
   */
  async acceptVerification(verificationId) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/accept`
    );
    return response.data;
  }

  /**
   * Start verification (on-site)
   */
  async startVerification(verificationId, gpsLocation) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/start`,
      { gpsLocation }
    );
    return response.data;
  }

  /**
   * Analyze photo with AI
   */
  async analyzePhoto(verificationId, photoBase64, checklistItem, verificationType) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/analyze-photo`,
      { photoBase64, checklistItem, verificationType }
    );
    return response.data;
  }

  /**
   * Submit verification
   */
  async submitVerification(verificationId, data) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/submit`,
      data
    );
    return response.data;
  }

  /**
   * Get verifier's verifications
   */
  async getMyVerifications(status) {
    const response = await api.get(
      '/simple-verifications/my-verifications',
      { params: { status } }
    );
    return response.data;
  }

  /**
   * Get verifications for project
   */
  async getProjectVerifications(projectId) {
    const response = await api.get(
      `/simple-verifications/project/${projectId}`
    );
    return response.data;
  }

  /**
   * Get verification details
   */
  async getVerification(verificationId) {
    const response = await api.get(
      `/simple-verifications/${verificationId}`
    );
    return response.data;
  }

  /**
   * Approve verification
   */
  async approveVerification(verificationId, approvalNotes) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/approve`,
      { approvalNotes }
    );
    return response.data;
  }

  /**
   * Dispute verification
   */
  async disputeVerification(verificationId, disputeReason) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/dispute`,
      { disputeReason }
    );
    return response.data;
  }

  /**
   * Analyze photo within conversational flow (supports multipart or JSON)
   */
  async analyzeConversationPhoto(verificationId, { photoBase64, conversationStep, verificationType, checklistContext, previousMessages }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/analyze-conversation-photo`,
      { photoBase64, conversationStep, verificationType, checklistContext, previousMessages }
    );
    return response.data;
  }

  /**
   * Generate AI-powered verification suggestions based on milestone context
   */
  async generateVerificationSuggestions(verificationId) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/generate-verification-suggestions`
    );
    return response.data;
  }

  /**
   * Generate milestone-based verification report with photos (multipart)
   */
  async milestoneVerificationAnalysis(verificationId, { verificationFocus, additionalNotes, photos }) {
    const formData = new FormData();
    formData.append('verificationFocus', verificationFocus);
    if (additionalNotes) formData.append('additionalNotes', additionalNotes);
    if (photos) {
      photos.forEach(photo => formData.append('photos', photo));
    }
    const response = await api.post(
      `/simple-verifications/${verificationId}/milestone-verification-analysis`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * AI-assisted report editing
   */
  async aiEditReport(verificationId, { currentReport, editInstructions, verificationFocus, milestoneName, photoCount }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/ai-edit-report`,
      { currentReport, editInstructions, verificationFocus, milestoneName, photoCount }
    );
    return response.data;
  }

  /**
   * Process conversational AI interaction (General tier)
   */
  async aiConversation(verificationId, { userMessage, conversationStep, checklistData, photos, previousMessages }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/ai-conversation`,
      { userMessage, conversationStep, checklistData, photos, previousMessages }
    );
    return response.data;
  }

  /**
   * Analyze photo with building codes context (Professional tier)
   */
  async analyzeProfessionalPhoto(verificationId, { photoBase64, conversationStep, buildingCode, verificationType, previousMessages }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/analyze-professional-photo`,
      { photoBase64, conversationStep, buildingCode, verificationType, previousMessages }
    );
    return response.data;
  }

  /**
   * Process professional conversational AI interaction (Professional tier)
   */
  async aiProfessionalConversation(verificationId, { userMessage, conversationStep, currentBuildingCode, complianceData, deficiencies, photos, previousMessages }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/ai-professional-conversation`,
      { userMessage, conversationStep, currentBuildingCode, complianceData, deficiencies, photos, previousMessages }
    );
    return response.data;
  }

  /**
   * Submit conversational verification report (General tier)
   */
  async submitConversational(verificationId, { conversationMessages, photos, checklistData, verificationDuration }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/submit-conversational`,
      { conversationMessages, photos, checklistData, verificationDuration }
    );
    return response.data;
  }

  /**
   * Submit professional verification report (Professional tier)
   */
  async submitProfessional(verificationId, { conversationMessages, photos, buildingCodes, complianceData, deficiencies, digitalSignature, verifierInfo, verificationDuration }) {
    const response = await api.post(
      `/simple-verifications/${verificationId}/submit-professional`,
      { conversationMessages, photos, buildingCodes, complianceData, deficiencies, digitalSignature, verifierInfo, verificationDuration }
    );
    return response.data;
  }
}

export default new SimpleVerificationService();
